import React, { FC, useState } from 'react';
import { useQuery } from '@apollo/client';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, IconButton, Typography } from '@mui/material';
import {
  Controller,
  FieldPathByValue,
  get,
  useFormContext,
} from 'react-hook-form';
import { defaultOperators } from 'src/components/Forms/CustomFormulaForm/Filters/FilterCollapse';
import {
  InnerContainer,
  Input,
  TitleContainer,
} from 'src/components/Forms/CustomFormulaForm/Filters/FilterCollapse/styled';
import { MultipleAutocomplete } from 'src/components/Forms/FormInputs/MultipleAutocomplete';
import { MultiValueInput } from 'src/components/Forms/FormInputs/MultiValueInput';
import { RegionSearchInput } from 'src/components/Forms/FormInputs/RegionSearchInput';
import { Select } from 'src/components/Forms/FormInputs/Select';
import { useDebounceFn } from 'src/hooks/useDebounceFn';
import { GET_FILTER_AVAILABLE_VALUES } from 'src/utils/gql/queries';
import {
  ConnectorType,
  FilterValueType,
  GetFilterAvailableValuesVariables,
  MetricsByConnectorType,
  Query,
} from 'src/utils/gql/types';

import { DashboardForm, FilterWithValue, OperatorEnum } from '../../types';

interface RuleProps {
  ruleName: string;
  filterPath: FieldPathByValue<
    DashboardForm,
    { operator: string; values: string[] }
  >;
  onDelete: () => void;
  selectedDimensionFilter: FilterWithValue;
}

export const Rule: FC<RuleProps> = ({
  filterPath,
  ruleName,
  selectedDimensionFilter,
  onDelete,
}) => {
  const {
    watch,
    setValue,
    control,
    formState: { errors },
  } = useFormContext<DashboardForm>();

  const isCustomFormula =
    selectedDimensionFilter.connectorType ===
    MetricsByConnectorType.CUSTOM_FORMULA;

  const { data, loading: isAvailableValuesLoading } = useQuery<
    Pick<Query, 'getFilterAvailableValues'>,
    GetFilterAvailableValuesVariables
  >(GET_FILTER_AVAILABLE_VALUES, {
    variables: isCustomFormula
      ? undefined
      : {
          filterKey: selectedDimensionFilter.key,
          connectorType: selectedDimensionFilter.connectorType as ConnectorType,
        },
    skip:
      isCustomFormula ||
      !selectedDimensionFilter.shouldAvailableValuesBeRequested,
  });

  // Build options immutably and dedupe by key to avoid React duplicate key warnings
  const availableValues = React.useMemo(() => {
    const base = selectedDimensionFilter.availableValues ?? [];
    const fetched = (data?.getFilterAvailableValues ?? []).map((value) => ({
      key: value,
      label: value,
    }));

    const map = new Map<string, { key: string; label: string }>();
    for (const opt of base) {
      if (opt?.key)
        map.set(opt.key, { key: opt.key, label: opt.label ?? opt.key });
    }
    for (const opt of fetched) {
      if (opt?.key && !map.has(opt.key)) map.set(opt.key, opt);
    }
    return Array.from(map.values());
  }, [selectedDimensionFilter.availableValues, data?.getFilterAvailableValues]);

  const [inputValue, setInputValue] = useState<string[]>(
    watch(`${filterPath}.values`),
  );

  const onChangeOperator = () => {
    setInputValue(['']);
    setValue(`${filterPath}.values`, []);
  };

  const operator = watch(`${filterPath}.operator`);
  const valuesInputError = get(errors, `${filterPath}.values`);

  const inputType =
    selectedDimensionFilter?.expectedReturnValueType ===
    FilterValueType.ArrayInt
      ? 'number'
      : 'text';

  const hasAvailableValues = !!availableValues.length;
  const inOrNotIn =
    operator === OperatorEnum.In || operator === OperatorEnum.NotIn;

  // Special case per user_region: usa SearchInput invece di dropdown
  const isUserRegionFilter = selectedDimensionFilter.key === 'user_region';

  const showMultiAutocomplete =
    (!selectedDimensionFilter || hasAvailableValues) &&
    inOrNotIn &&
    !isUserRegionFilter;
  const showMultiValueInput =
    selectedDimensionFilter &&
    !hasAvailableValues &&
    inOrNotIn &&
    !isUserRegionFilter;
  const showSearchInput = isUserRegionFilter && inOrNotIn;
  const showInput = !inOrNotIn && !isUserRegionFilter;

  const handleInputChange = ({
    val,
    onChange,
  }: {
    val: string[];
    onChange: (...event: any[]) => void;
  }) => {
    onChange(val);
  };

  const debFunction = useDebounceFn(handleInputChange, 600);

  const selectOptions =
    selectedDimensionFilter?.operators &&
    selectedDimensionFilter.operators.length > 0
      ? selectedDimensionFilter.operators
      : [];

  return (
    <Box>
      <TitleContainer>
        <Typography>{ruleName}</Typography>
        <IconButton onClick={onDelete}>
          <DeleteIcon />
        </IconButton>
      </TitleContainer>
      <InnerContainer>
        <Select
          name={`${filterPath}.operator`}
          options={selectOptions}
          onManualChange={onChangeOperator}
          disabled={selectOptions.length === 0}
        />

        {showMultiAutocomplete && (
          <MultipleAutocomplete
            name={`${filterPath}.values`}
            options={availableValues}
            withDebounce
            loading={isAvailableValuesLoading}
          />
        )}

        {showSearchInput && (
          <RegionSearchInput
            name={`${filterPath}.values`}
            placeholder=""
            helperText=""
          />
        )}

        {showInput && (
          <Controller
            control={control}
            name={`${filterPath}.values`}
            render={({ field: { onChange, onBlur } }) => (
              <Input
                autoComplete="off"
                placeholder="Write a value"
                error={!!valuesInputError?.message}
                value={inputValue}
                onChange={(e) => {
                  const val = e.target.value ? [e.target.value] : [];
                  setInputValue(val);

                  debFunction({ val, onChange });
                }}
                onBlur={onBlur}
                type={inputType}
              />
            )}
          />
        )}
        {showMultiValueInput && (
          <MultiValueInput name={`${filterPath}.values`} type={inputType} />
        )}
      </InnerContainer>
    </Box>
  );
};
